/**
 * Centralized authentication utilities
 * Provides consistent authentication state checking across the application
 *
 * This ensures that authentication state is consistent between:
 * - Main application login/logout
 * - IdTheft flow authentication
 * - Redux state management
 * - localStorage persistence
 */

/**
 * Check if a user is authenticated based on user object from Redux state
 * This is the primary authentication check used throughout the app
 * @param {Object} user - User object from Redux state
 * @returns {boolean} - True if user is authenticated, false otherwise
 */
export const isAuthenticated = (user) => {
  // Primary check: user object from Redux state has email
  const reduxAuth = !!(user && user.email);

  // If Redux indicates authenticated, return true
  if (reduxAuth) return true;

  // Fallback: check localStorage for consistency (used by AuthProvider)
  if (typeof window !== 'undefined') {
    const loggedUser = localStorage.getItem('ri:loggedUser');
    if (loggedUser) {
      try {
        const cachedUser = JSON.parse(loggedUser);
        return !!(cachedUser && cachedUser.email);
      } catch (e) {
        // Invalid localStorage data, ignore
      }
    }
  }

  return false;
};

/**
 * Check if current user has admin role
 * @param {Object} user - User object from Redux state
 * @returns {boolean} - True if user is admin, false otherwise
 */
export const isAdmin = (user) => {
  return isAuthenticated(user) && user.role === 'admin';
};

/**
 * Check if user is authenticated based on Redux state structure
 * This is an alias for isAuthenticated for backward compatibility
 * @param {Object} userState - User state from Redux
 * @returns {boolean} - True if user is authenticated, false otherwise
 */
export const checkUserAuthentication = (userState) => {
  return isAuthenticated(userState);
};

/**
 * Get user authentication status from localStorage only (fallback/debugging)
 * @returns {boolean} - True if logged user exists in localStorage
 */
export const checkLocalStorageAuth = () => {
  if (typeof window === 'undefined') return false;

  const loggedUser = localStorage.getItem('ri:loggedUser');
  if (!loggedUser) return false;

  try {
    const user = JSON.parse(loggedUser);
    return !!(user && user.email);
  } catch (e) {
    return false;
  }
};

export const hasValidToken = () => {
  if (typeof window === 'undefined') return false;

  const token = localStorage.getItem('token');
  return !!(token && token.trim() !== '');
};

/**
 * Clear all authentication-related data from localStorage
 * This ensures consistent logout behavior across the entire application
 * Should be called by all logout functions to maintain consistency
 */
export const clearAllAuthData = () => {
  if (typeof window === 'undefined') return;

  // Clear all authentication-related localStorage items
  localStorage.removeItem('ri:loggedUser'); // AuthProvider temp storage
  localStorage.removeItem('token'); // API authentication token
  localStorage.removeItem('ri-store'); // Redux store persistence
  localStorage.removeItem('returnUrl'); // Return URL for redirects

  // Clear any other auth-related items that might exist
  // (Add more here as needed)
};
